import type { ImportQueue } from "./import-queue.ts";
import type { Transaction } from "./finance.ts";

export type ImportReviewDraft = {
  protocolVersion?: 198;
  file: File;
  requestId?: string;
  originalRows: Transaction[];
  rows: Transaction[];
  excludedIndexes: number[];
  createdAt: string;
  updatedAt: string;
  submission?: ImportSubmission;
};

export type ImportSubmission = {
  requestId: string;
  rows: Transaction[];
  detectedCount: number;
  excluded: { sourceRowNumber: number; row: Transaction }[];
};

const DB_NAME = "ek-finance-import-drafts";
const STORE_NAME = "review-drafts";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB unavailable")); return; }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Unable to open draft storage"));
    request.onblocked = () => reject(new Error("Draft storage blocked by another tab"));
  });
}

async function readDraft(key: string): Promise<ImportReviewDraft | null> {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve((request.result as ImportReviewDraft | undefined) || null);
      request.onerror = () => reject(request.error || new Error("Unable to read draft"));
    });
  } finally { db.close(); }
}

async function writeDraft(key: string, draft: ImportReviewDraft | null) {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      if (draft) store.put(draft, key);
      else store.delete(key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error("Unable to save draft"));
      transaction.onabort = () => reject(transaction.error || new Error("Draft write aborted"));
    });
  } finally { db.close(); }
}


const draftTasks = new Map<string, Promise<unknown>>();
function serialDraft<T>(key: string, operation: () => Promise<T>): Promise<T> {
  const task = (draftTasks.get(key) || Promise.resolve()).catch(() => undefined).then(operation);
  draftTasks.set(key, task);
  const cleanup = () => { if (draftTasks.get(key) === task) draftTasks.delete(key); };
  void task.then(cleanup, cleanup);
  return task;
}
export async function loadImportReviewDraft(key: string) {
  return serialDraft(key, async () => {
  const draft = await readDraft(key);
  if (draft && !draft.requestId) { draft.requestId = crypto.randomUUID(); await writeDraft(key, draft); }
  return draft;
  });
}

export async function startImportReviewDraft(key: string, file: File, rows: Transaction[], requestId: string = crypto.randomUUID()) {
  return serialDraft(key, async () => {
  const timestamp = new Date().toISOString();
  const draft: ImportReviewDraft = { protocolVersion: 198, file, requestId, originalRows: rows, rows, excludedIndexes: [], createdAt: timestamp, updatedAt: timestamp };
  await writeDraft(key, draft);
  return draft;
  });
}

export async function saveImportReviewDraft(key: string, rows: Transaction[], excludedIndexes: number[]) {
  return serialDraft(key, async () => {
  const existing = await readDraft(key);
  if (!existing || existing.submission) return;
  await writeDraft(key, { ...existing, rows, excludedIndexes, updatedAt: new Date().toISOString() });
  });
}

export async function lockImportReviewSubmission(key: string, fallback: ImportReviewDraft, submission: ImportSubmission) {
  return serialDraft(key, async () => {
    const existing = await readDraft(key);
    const draft = existing || fallback;
    if (draft.requestId !== submission.requestId) throw new Error("Import draft identity changed");
    if (draft.submission) return draft;
    const locked: ImportReviewDraft = { ...draft, protocolVersion: 198, submission, updatedAt: new Date().toISOString() };
    await writeDraft(key, locked);
    return locked;
  });
}

export async function unlockRejectedImportReview(key: string, requestId: string) {
  return serialDraft(key, async () => {
    const draft = await readDraft(key);
    if (!draft || draft.requestId !== requestId) throw new Error("Import draft identity changed");
    const { submission: _submitted, ...editable } = draft;
    await writeDraft(key, editable);
    return editable;
  });
}

export async function clearImportReviewDraft(key: string) {
  return serialDraft(key, async () => {
  await writeDraft(key, null);
  });
}

export async function loadImportQueue(key: string): Promise<ImportQueue | null> {
  const db = await openDatabase();
  try { return await new Promise((resolve,reject) => {
    const request = db.transaction(STORE_NAME,"readonly").objectStore(STORE_NAME).get("queue:"+key);
    request.onsuccess = () => resolve(request.result || null); request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
export async function saveImportQueue(key: string, queue: ImportQueue | null) {
  const db = await openDatabase();
  try { await new Promise<void>((resolve,reject) => {
    const transaction = db.transaction(STORE_NAME,"readwrite"), store=transaction.objectStore(STORE_NAME);
    if(queue)store.put(queue,"queue:"+key);else store.delete("queue:"+key);
    transaction.oncomplete=()=>resolve();transaction.onerror=transaction.onabort=()=>reject(transaction.error);
  }); } finally { db.close(); }
}
